import {
  INSTRUMENT_VERSION,
  QUESTIONS,
  DIMENSIONS,
  MISMATCH_PAIRS,
  FRICTION_CONTROL_ACTION,
  ALTERNATIVE_CONSTRAINT_ACTION,
} from "../data/review";

const QUESTION_MAP = new Map(QUESTIONS.map((question) => [question.id, question]));
const VALID_VALUES = new Set([1, 2, 3, 4, 5, "NA", "NV"]);

export const round1 = (number) => Math.round((number + Number.EPSILON) * 10) / 10;
export const isScoredResponse = (value) => [1, 2, 3, 4, 5].includes(value);

export const bandForIndex = (index) => {
  if (index >= 75) return "holding-up";
  if (index >= 60) return "mostly-working";
  if (index >= 40) return "breaking-down";
  return "getting-in-the-way";
};

export const bandForFrictionIndex = (index) => {
  if (index >= 75) return "high";
  if (index >= 50) return "material";
  if (index >= 25) return "some";
  return "low";
};

export const convertResponse = (question, response) => {
  if (!isScoredResponse(response)) return null;
  if (question.kind === "friction") {
    return question.polarity === "positive" ? (5 - response) * 25 : (response - 1) * 25;
  }
  return question.polarity === "positive" ? (response - 1) * 25 : (5 - response) * 25;
};

export const evaluateMismatch = (positiveResponse, negativeResponse) => {
  if (!isScoredResponse(positiveResponse) || !isScoredResponse(negativeResponse)) {
    return "not-available";
  }
  const positiveHigh = positiveResponse === 4 || positiveResponse === 5;
  const negativeHigh = negativeResponse === 4 || negativeResponse === 5;
  if (positiveHigh && negativeHigh) return "strong";
  if (
    (positiveResponse === 3 && negativeHigh) ||
    (negativeResponse === 3 && positiveHigh)
  ) {
    return "possible";
  }
  return "none";
};

export const selectStrongestCondition = (eligible) => {
  if (eligible.length === 0) return null;
  if (eligible.length === 1) return eligible[0].dimension;
  const descending = [...eligible].sort((a, b) => b.index - a.index);
  return descending[0].index - descending[1].index > 2.5
    ? descending[0].dimension
    : null;
};

export const selectPrimarySignal = (eligible) => {
  if (eligible.length < 2) return null;
  const ascending = [...eligible].sort((a, b) => a.index - b.index);
  return ascending[0].index < 60 && ascending[1].index - ascending[0].index >= 10
    ? ascending[0].dimension
    : null;
};

export const selectOverallCategory = ({
  unscoredCount,
  eligibleClarityCount,
  frictionAnsweredCount,
  below60Count,
  frictionIndex,
}) => {
  if (
    unscoredCount > 5 ||
    eligibleClarityCount < 4 ||
    frictionAnsweredCount < 2
  ) {
    return "not-enough-information";
  }
  if (below60Count >= 3 && frictionIndex >= 25) return "several-breakdowns";
  if (below60Count >= 1 && below60Count <= 2 && frictionIndex >= 25) {
    return "focused-breakdown";
  }
  if (below60Count === 0 && frictionIndex >= 50) return "another-cause";
  if (below60Count >= 1 && frictionIndex < 25) return "gaps-low-friction";
  return "mostly-clear";
};

export function validateSubmission({
  instrumentVersion,
  areaAssessed,
  recurringConcern,
  writtenExample,
  answers,
}) {
  const errors = [];
  if (instrumentVersion !== INSTRUMENT_VERSION) errors.push("unsupported-version");
  if (!areaAssessed || !String(areaAssessed).trim()) errors.push("missing-area");
  if (areaAssessed && String(areaAssessed).length > 120) errors.push("area-too-long");
  if (recurringConcern && String(recurringConcern).length > 300) errors.push("concern-too-long");
  if (writtenExample && String(writtenExample).length > 500) errors.push("example-too-long");
  if (!Array.isArray(answers)) {
    errors.push("answers-malformed");
    return errors;
  }

  const ids = answers.map((answer) => answer && answer.questionId);
  if (
    answers.some(
      (answer) =>
        !answer ||
        typeof answer.questionId !== "string" ||
        !VALID_VALUES.has(answer.value)
    )
  ) {
    errors.push("malformed-answer");
  }
  if (new Set(ids).size !== ids.length) errors.push("duplicate-question-id");

  const requiredIds = QUESTIONS.map((question) => question.id);
  if (
    ids.length !== requiredIds.length ||
    !requiredIds.every((questionId) => ids.includes(questionId))
  ) {
    errors.push("missing-question-id");
  }
  if (ids.some((questionId) => !QUESTION_MAP.has(questionId))) {
    errors.push("unsupported-question-id");
  }
  return errors;
}

export function selectRecommendedActions({
  dimensions,
  friction,
  mismatches,
  primarySignal,
  overallCategory,
}) {
  const eligibleAscending = dimensions
    .filter((dimension) => dimension.band !== "insufficient")
    .sort((a, b) => a.index - b.index);
  const weakAreas = eligibleAscending.filter((dimension) => dimension.index < 60);
  const actions = [];

  if (overallCategory === "another-cause") {
    actions.push(ALTERNATIVE_CONSTRAINT_ACTION);
  } else {
    const firstArea = primarySignal || weakAreas[0]?.dimension;
    if (firstArea) {
      actions.push(DIMENSIONS.find((dimension) => dimension.key === firstArea).action);
    }
  }

  const strongMismatch = mismatches.find((mismatch) => mismatch.level === "strong");
  if (strongMismatch) {
    const areaName =
      strongMismatch.area === "friction"
        ? "day-to-day friction"
        : DIMENSIONS.find((dimension) => dimension.key === strongMismatch.area).name;
    actions.push(
      `Check one recent example from ${areaName} to see why the answers point in different directions.`
    );
  }

  if (overallCategory !== "another-cause") {
    const firstArea = primarySignal || weakAreas[0]?.dimension;
    const secondArea = weakAreas.find((dimension) => dimension.dimension !== firstArea);
    if (secondArea) {
      actions.push(
        DIMENSIONS.find((dimension) => dimension.key === secondArea.dimension).action
      );
    }
  }

  if (friction.band === "material" || friction.band === "high") {
    actions.push(FRICTION_CONTROL_ACTION);
  }

  return [...new Set(actions)].slice(0, 3);
}

export function computeProfile({ areaAssessed, recurringConcern, writtenExample, answers }) {
  const responseMap = new Map(answers.map((answer) => [answer.questionId, answer.value]));

  const dimensions = DIMENSIONS.map((dimension) => {
    const converted = dimension.itemIds
      .map((questionId) =>
        convertResponse(QUESTION_MAP.get(questionId), responseMap.get(questionId))
      )
      .filter((value) => value !== null);
    if (converted.length < 2) {
      return {
        dimension: dimension.key,
        answeredCount: converted.length,
        index: null,
        band: "insufficient",
      };
    }
    const index = round1(
      converted.reduce((total, value) => total + value, 0) / converted.length
    );
    return {
      dimension: dimension.key,
      answeredCount: converted.length,
      index,
      band: bandForIndex(index),
    };
  });

  const frictionValues = QUESTIONS.filter((question) => question.kind === "friction")
    .map((question) => convertResponse(question, responseMap.get(question.id)))
    .filter((value) => value !== null);
  const friction =
    frictionValues.length < 2
      ? {
          answeredCount: frictionValues.length,
          index: null,
          band: "unclassified",
          limitedEvidence: false,
        }
      : (() => {
          const index = round1(
            frictionValues.reduce((total, value) => total + value, 0) /
              frictionValues.length
          );
          return {
            answeredCount: frictionValues.length,
            index,
            band: bandForFrictionIndex(index),
            limitedEvidence: frictionValues.length === 2,
          };
        })();

  const availability = answers.reduce(
    (counts, answer) => {
      if (answer.value === "NA") counts.occurrenceUnavailable += 1;
      if (answer.value === "NV") counts.visibilityUnavailable += 1;
      return counts;
    },
    { occurrenceUnavailable: 0, visibilityUnavailable: 0 }
  );
  availability.unscoredCount =
    availability.occurrenceUnavailable + availability.visibilityUnavailable;

  const eligible = dimensions.filter((dimension) => dimension.band !== "insufficient");
  const below60 = eligible.filter((dimension) => dimension.index < 60);

  const overallCategory = selectOverallCategory({
    unscoredCount: availability.unscoredCount,
    eligibleClarityCount: eligible.length,
    frictionAnsweredCount: friction.answeredCount,
    below60Count: below60.length,
    frictionIndex: friction.index,
  });

  const mismatches = MISMATCH_PAIRS.map((pair) => ({
    ...pair,
    level: evaluateMismatch(
      responseMap.get(pair.positiveId),
      responseMap.get(pair.negativeId)
    ),
  }));
  const primarySignal = selectPrimarySignal(eligible);
  const strongestCondition = selectStrongestCondition(eligible);
  const recommendedActions = selectRecommendedActions({
    dimensions,
    friction,
    mismatches,
    primarySignal,
    overallCategory,
  });

  return {
    instrumentVersion: INSTRUMENT_VERSION,
    areaAssessed,
    recurringConcern: recurringConcern || undefined,
    writtenExample: writtenExample || undefined,
    overallCategory,
    dimensions,
    friction,
    availability,
    mismatches,
    primarySignal,
    strongestCondition,
    recommendedActions,
  };
}
